import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    Perspective,
    PerspectiveName,
    Perspectives,
    ScreenRegion
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/layout/Perspective';
import {
    Panel,
    PanelGroup,
    PanelId,
    PanelKind,
    PanelLayoutState,
    Section,
    SectionKind,
    SectionRun,
    SectionVisibility
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {FlowCanvas} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/canvas/FlowCanvas';
import {FlowEditorGlass} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/FlowEditorGlass';
import {FlowPropertiesPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/FlowPropertiesPanel';
import {ConsolePanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/console/ConsolePanel';
import {DebugDataPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/debugData/DebugDataPanel';
import {action} from 'mobx';
import * as React from 'react';

export class PerspectiveController
{
    public init():void
    {
        const flowPerspective = this._getFlowPerspective();

        this.registerPerspective(flowPerspective);
        this.switchTo(Perspectives.FLOW_TAB);
    }

    private _getFlowPerspective():Perspective
    {
        const FLOW_TAB = Perspectives.FLOW_TAB;
        const {perspective} = controllers.ui;

        const newPerspective = perspective
            .newPerspective(FLOW_TAB)
            .setContent(React.createElement(FlowCanvas))
            .addPanel(ScreenRegion.East, PanelKind.FlowProperties, 'Block Properties', React.createElement(FlowPropertiesPanel))
            .addPanel(ScreenRegion.South, PanelKind.FlowConsole, 'Messages', React.createElement(ConsolePanel))
            .addPanel(ScreenRegion.South, PanelKind.FlowDebugData, 'Debug Data', React.createElement(DebugDataPanel))
            .addGlass(React.createElement(FlowEditorGlass));

        const westSection = newPerspective.findSection(ScreenRegion.West);
        const eastSection = newPerspective.findSection(ScreenRegion.East);

        westSection.visibility = SectionVisibility.Hidden;
        eastSection.visibility = SectionVisibility.Hidden;

        return newPerspective;
    }

    public newPerspective(name:PerspectiveName):Perspective
    {
        return Perspective.named(name);
    }

    public switchTo(name:PerspectiveName):void
    {
        const p = this.getPerspective(name);
        if( !p )
            return;

        this._selectFirstPanelIfNoneSelected(p, ScreenRegion.East);
        this._selectFirstPanelIfNoneSelected(p, ScreenRegion.West);
        this._selectFirstPanelIfNoneSelected(p, ScreenRegion.South);
        this._selectFirstPanelIfNoneSelected(p, ScreenRegion.Content);

        this.setActivePerspective(p);
    }

    public findPanelById(panelId:PanelId):Panel | null
    {
        const allPanels = this._getAllPanelsInAllPerspectives();
        return allPanels.find((p:Panel) => (p.id === panelId));
    }

    private _getAllPanelsInAllPerspectives():Panel[]
    {
        const allPanels:Panel[] = [];
        const perspectives = this._getState().allPerspectives;

        perspectives.map((p:Perspective) =>
        {
            p.panels.map((panel:Panel) =>
            {
                allPanels.push(panel);
            });
        });

        return allPanels;
    }

    private _getAll():Panel[]
    {
        const allPanels:Panel[] = [];
        const perspectives = this._getState().allPerspectives;

        perspectives.map((p:Perspective) =>
        {
            p.panels.map((panel:Panel) =>
            {
                allPanels.push(panel);
            });
        });

        return allPanels;
    }

    public getAllPerspectives():Perspective[]
    {
        return this._getState().allPerspectives;
    }

    public clearAllPerspectives():void
    {
        this._getState().allPerspectives = [];
    }

    public getActivePerspective():Perspective | null
    {
        return this._getState().activePerspective;
    }

    @action.bound
    public setActivePerspective(p:Perspective):void
    {
        this._getState().activePerspective = p;
    }

    public registerPerspective(newPerspective:Perspective):void
    {
        this._getState().allPerspectives.push(newPerspective);
    }

    private _selectFirstPanelIfNoneSelected(p:Perspective, region:ScreenRegion)
    {
        const pg = p.getPanelGroup(region);

        if( !pg.selectedPanel )
            pg.selectFirstPanel();
    }

    public hideAllActivePanels()
    {
        if( this.isAreActivePanelsVisible() )
            this._updateAllPanelAreasVisibility(false);
    }

    public showAllActivePanels()
    {
        if( !this.isAreActivePanelsVisible() )
            this._updateAllPanelAreasVisibility(true);
    }

    public isAreActivePanelsVisible()
    {
        const p = this.getActivePerspective();
        if( !p )
            return false;

        return p.panelSet.isPanelAreasVisible;
    }

    @action.bound
    private _updateAllPanelAreasVisibility(areVisible:boolean)
    {
        const p = this.getActivePerspective();
        if( !p )
            return false;

        p.panelSet.isPanelAreasVisible = areVisible;
        p.sections.map((s:Section) =>
        {
            if( s.kind === SectionKind.PanelArea )
                s.visibility = areVisible ? SectionVisibility.Visible : SectionVisibility.Hidden;
        });
    }

    public isSectionRunInActivePerspective(sectionRun:SectionRun):boolean
    {
        const p = this.getActivePerspective();
        if( !p )
            return false;

        const idx = p.sectionRuns.findIndex((r:SectionRun) => (r.id === sectionRun.id));
        return idx !== -1;
    }

    public isPanelGroupInActivePerspective(group:PanelGroup):boolean
    {
        const p = this.getActivePerspective();
        if( !p )
            return false;

        const idx = p.panelGroups.findIndex((g:PanelGroup) => (g.id === group.id));
        return idx !== -1;
    }

    public findPanelPerspective(panel:Panel):Perspective | null
    {
        const perspectives = this.getAllPerspectives();

        for( let p of perspectives )
        {
            const idx = p.panelGroups.findIndex((g:PanelGroup) => (g.id === panel.panelGroup.id));
            if( idx !== -1 )
                return p;
        }

        return null;
    }

    public getPerspective(name:PerspectiveName):Perspective | null
    {
        const perspectives = this._getState().allPerspectives;
        const perspective = perspectives.find((p:Perspective) => (p.name === name));

        if( !perspective )
        {
            console.error(`Error: cannot find perspective by name: ${name}`);
            return null;
        }

        return perspective;
    }

    private _getState():PanelLayoutState
    {
        return rootState.ui.visualDesigner.workbench.layout;
    }
}
